export type SettingsTone = "brand" | "primary" | "success" | "warning" | "muted" | "secondary";
export type SettingsTab = "overview" | "concepts" | "roles" | "copy" | "image" | "photos" | "week" | "retro" | "pricing";
export type FieldKind = "text" | "select" | "prompt";

export type SettingsIconName =
  | "Workflow"
  | "Sparkles"
  | "MessageSquare"
  | "FileText"
  | "Images"
  | "Camera"
  | "CalendarDays"
  | "RotateCcw"
  | "DollarSign"
  | "SlidersHorizontal"
  | "Send";

export interface FieldMeta {
  key: string;
  label: string;
  hint: string;
  tip: string;
  kind: FieldKind;
  options?: { value: string; label: string }[];
}

export interface TabMeta {
  id: SettingsTab;
  label: string;
  blurb: string;
  tone: SettingsTone;
  icon: SettingsIconName;
}

export interface PipelineChip {
  id: string;
  tab: SettingsTab;
  label: string;
  uses: string;
  tone: SettingsTone;
  icon: SettingsIconName;
}

export const TONE_CLASS: Record<
  SettingsTone,
  { bar: string; head: string; icon: string; chip: string }
> = {
  brand: {
    bar: "border-l-brand",
    head: "bg-brand/10",
    icon: "text-brand",
    chip: "bg-brand/15 text-brand",
  },
  primary: {
    bar: "border-l-primary",
    head: "bg-primary/10",
    icon: "text-primary",
    chip: "bg-primary/10 text-foreground",
  },
  success: {
    bar: "border-l-success",
    head: "bg-success/10",
    icon: "text-success",
    chip: "bg-success/15 text-success",
  },
  warning: {
    bar: "border-l-warning",
    head: "bg-warning/10",
    icon: "text-warning",
    chip: "bg-warning/15 text-warning",
  },
  muted: {
    bar: "border-l-muted-foreground/40",
    head: "bg-muted",
    icon: "text-muted-foreground",
    chip: "bg-muted text-muted-foreground",
  },
  secondary: {
    bar: "border-l-border",
    head: "bg-secondary",
    icon: "text-secondary-foreground",
    chip: "bg-secondary text-secondary-foreground",
  },
};

export const TABS: TabMeta[] = [
  {
    id: "overview",
    label: "Przegląd",
    blurb: "Który model i prompt wchodzi w który krok. Tu nic nie edytujesz, tylko widzisz zależności.",
    tone: "brand",
    icon: "Workflow",
  },
  {
    id: "concepts",
    label: "Koncepcje",
    blurb: "Krok po briefie: 4 karty kątów. Flash pisze haki. Fallback, gdy Gemini padnie.",
    tone: "brand",
    icon: "Sparkles",
  },
  {
    id: "roles",
    label: "Role i pamięć",
    blurb: "Po wyborze karty: panel ról (Pro) + co z retro wraca do kolejnej karuzeli.",
    tone: "primary",
    icon: "MessageSquare",
  },
  {
    id: "copy",
    label: "Copy i typy",
    blurb: "Synteza slajdów. Reguły wspólne plus osobny prompt na typ (story ma reveal, vs-echo ma STOP).",
    tone: "success",
    icon: "FileText",
  },
  {
    id: "image",
    label: "Obraz",
    blurb: "Gemini rysuje tło bez liter. Pillow nakłada polski tekst. Wybierz model z listy: Lite jest najszybszy.",
    tone: "warning",
    icon: "Images",
  },
  {
    id: "photos",
    label: "Zdjęcia",
    blurb: "Model Vision taguje wrzutkę. Ty akceptujesz do biblioteki.",
    tone: "warning",
    icon: "Camera",
  },
  {
    id: "week",
    label: "Tydzień",
    blurb: "Cele mixu 40/40/20 i tolerancja przy zatwierdzaniu planu.",
    tone: "primary",
    icon: "CalendarDays",
  },
  {
    id: "retro",
    label: "Retro i wnioski",
    blurb: "Po publikacji: lekcja. Pending nie uczy modelu, dopóki nie zatwierdzisz.",
    tone: "muted",
    icon: "RotateCcw",
  },
  {
    id: "pricing",
    label: "Cennik",
    blurb: "Stawki USD do stopki kosztu. Nie sterują modelem, tylko licznikiem.",
    tone: "secondary",
    icon: "DollarSign",
  },
];

export const PIPELINE_CHIPS: PipelineChip[] = [
  {
    id: "params",
    tab: "overview",
    label: "Parametry",
    uses: "Brief w studiu: ból, typ, źródła. Nie ma tu suwaków.",
    tone: "muted",
    icon: "SlidersHorizontal",
  },
  {
    id: "concepts",
    tab: "concepts",
    label: "Koncepcje",
    uses: "model koncepcji + prompt koncepcji. Dochodzą reguły wspólne.",
    tone: "brand",
    icon: "Sparkles",
  },
  {
    id: "copy",
    tab: "roles",
    label: "Treść",
    uses: "Najpierw panel ról i pamięć, potem synteza slajdów i prompt typu.",
    tone: "primary",
    icon: "MessageSquare",
  },
  {
    id: "images",
    tab: "image",
    label: "Grafiki",
    uses: "Model obrazu. Wejście dopiero po zatwierdzeniu copy.",
    tone: "warning",
    icon: "Images",
  },
  {
    id: "publish",
    tab: "overview",
    label: "Publikacja",
    uses: "Kolejka i Graph. Tu nie ma promptu ani modelu.",
    tone: "muted",
    icon: "Send",
  },
  {
    id: "retro",
    tab: "retro",
    label: "Retro",
    uses: "Model retro + prompt retro + zatwierdzone wnioski.",
    tone: "muted",
    icon: "RotateCcw",
  },
];

export const OVERVIEW_CARDS: {
  tab: SettingsTab;
  step: string;
  title: string;
  modelKeys: string[];
  promptKeys: string[];
  extra: string;
  tone: SettingsTone;
}[] = [
  {
    tab: "concepts",
    step: "1",
    title: "Koncepcje",
    modelKeys: ["model.concepts", "model.concepts.fallback"],
    promptKeys: ["prompt.concepts", "prompt.rules_shared"],
    extra: "Pamięć nie wchodzi. 4 haki z korpusu i briefu.",
    tone: "brand",
  },
  {
    tab: "roles",
    step: "2",
    title: "Panel ról",
    modelKeys: ["model.discussion", "model.heavy.fallback"],
    promptKeys: ["prompt.discussion_panel", "prompt.rules_shared"],
    extra: "Pamięć (tylko approved) dokleja się do panelu.",
    tone: "primary",
  },
  {
    tab: "copy",
    step: "3",
    title: "Copy slajdów",
    modelKeys: ["model.copy", "model.heavy.fallback"],
    promptKeys: ["prompt.copy_synth", "prompt.rules_copy", "prompt.copy_review", "prompt.copy_revise", "prompt.type.*"],
    extra: "Typ story: ostatni slajd = odpowiedź. Inne typy: narzędzie / STOP.",
    tone: "success",
  },
  {
    tab: "image",
    step: "4",
    title: "Tło slajdu",
    modelKeys: ["model.image", "model.image.fallback"],
    promptKeys: ["prompt.design_review", "prompt.design_verify", "prompt.design_plan", "prompt.role.dyrektorka", "prompt.role.reklamowa"],
    extra: "Po PNG: dyrektorka i reklamowa oceniają zestaw, potem przed/po.",
    tone: "warning",
  },
  {
    tab: "overview",
    step: "5",
    title: "Publikacja",
    modelKeys: [],
    promptKeys: [],
    extra: "Celery + Graph. Zero ustawień na tej stronie.",
    tone: "muted",
  },
  {
    tab: "retro",
    step: "6",
    title: "Retro",
    modelKeys: ["model.retro", "model.heavy.fallback"],
    promptKeys: ["prompt.retro", "prompt.role.analityczka", "prompt.role.bart", "prompt.layout_plan"],
    extra: "Wniosek ląduje jako pending. Ty decydujesz, czy uczy dalej.",
    tone: "muted",
  },
];

const BOOL_OPTS = [
  { value: "true", label: "tak" },
  { value: "false", label: "nie" },
];

export const TEXT_MODEL_OPTIONS = [
  { value: "gemini-3.6-flash", label: "3.6 Flash (koncepcje, szybki)" },
  { value: "gemini-2.5-flash", label: "2.5 Flash (szybki zapas)" },
  { value: "gemini-2.5-flash-lite", label: "2.5 Flash Lite (najtańszy tekst)" },
  { value: "gemini-3.1-pro-preview", label: "3.1 Pro (role, copy, retro)" },
  { value: "gemini-2.5-pro", label: "2.5 Pro (zapas Pro)" },
];

export const IMAGE_MODEL_OPTIONS = [
  { value: "gemini-3.1-flash-lite-image", label: "3.1 Flash Lite Image (najszybszy, ~4 s / tło)" },
  { value: "gemini-3.1-flash-image", label: "3.1 Flash Image (balans)" },
  { value: "gemini-2.5-flash-image", label: "2.5 Flash Image (starszy zapas)" },
  { value: "gemini-3-pro-image", label: "3 Pro Image (jakość, wolny)" },
];

export const FIELDS: Record<string, FieldMeta> = {
  "model.concepts": {
    key: "model.concepts",
    label: "Model koncepcji",
    hint: "Pisze 4 haki po kliknięciu Złóż koncepcje.",
    tip: "Krok 1 w studiu. Tani Flash. Gdy padnie, wchodzi zapas poniżej.",
    kind: "select",
    options: TEXT_MODEL_OPTIONS,
  },
  "model.concepts.fallback": {
    key: "model.concepts.fallback",
    label: "Zapas koncepcji",
    hint: "Drugi model, gdy główny nie odpowie.",
    tip: "Nie używany, dopóki pierwszy call nie padnie.",
    kind: "select",
    options: TEXT_MODEL_OPTIONS,
  },
  "model.discussion": {
    key: "model.discussion",
    label: "Model panelu ról",
    hint: "Pro po wyborze jednej karty.",
    tip: "Jedna runda JSON-a od 5 ról. Cięższy i droższy niż koncepcje.",
    kind: "select",
    options: TEXT_MODEL_OPTIONS,
  },
  "model.copy": {
    key: "model.copy",
    label: "Model copy slajdów",
    hint: "Składa tekst slajdów z panelu i promptu typu.",
    tip: "Krok po rolach. Close (odpowiedź albo STOP) bierze z typu, nie zgaduje.",
    kind: "select",
    options: TEXT_MODEL_OPTIONS,
  },
  "model.retro": {
    key: "model.retro",
    label: "Model retro",
    hint: "Lekcja po publikacji, na Twoje żądanie.",
    tip: "Nie odpala się sam. Wniosek i tak czeka na zatwierdzenie.",
    kind: "select",
    options: TEXT_MODEL_OPTIONS,
  },
  "model.heavy.fallback": {
    key: "model.heavy.fallback",
    label: "Zapas dla Pro",
    hint: "Wspólny zapas panelu, copy i retro.",
    tip: "Gdy discussion / copy / retro padną, ten model wchodzi zamiast nich.",
    kind: "select",
    options: TEXT_MODEL_OPTIONS,
  },
  "model.image": {
    key: "model.image",
    label: "Model tła",
    hint: "Rysuje tło bez liter. Lite jest najszybszy.",
    tip: "Wejście dopiero po zatwierdzeniu copy. Polski tekst nakłada Pillow, nie ten model.",
    kind: "select",
    options: IMAGE_MODEL_OPTIONS,
  },
  "model.image.fallback": {
    key: "model.image.fallback",
    label: "Zapas obrazu",
    hint: "Drugi model tła, gdy pierwszy nie zwróci PNG.",
    tip: "Nie wołany, dopóki główny model nie padnie. Dwa razy ten sam model nie przyspiesza.",
    kind: "select",
    options: IMAGE_MODEL_OPTIONS,
  },
  "model.vision": {
    key: "model.vision",
    label: "Model tagowania zdjęć",
    hint: "Opisuje wrzutkę: osoba, tryb, lejek, tagi.",
    tip: "Celery skanuje inbox/zdjecia. Bez klucza jest heurystyka z nazwy pliku.",
    kind: "select",
    options: TEXT_MODEL_OPTIONS,
  },
  "week.mix.tofu": {
    key: "week.mix.tofu",
    label: "Cel TOFU %",
    hint: "Udział góry lejka w planie tygodnia.",
    tip: "Norma startowa 40. Ostrzeżenie, nie blokada, gdy zejdziesz poza tolerancję.",
    kind: "text",
  },
  "week.mix.mofu": {
    key: "week.mix.mofu",
    label: "Cel MOFU %",
    hint: "Udział środka lejka.",
    tip: "Karuzele i posty Arka prawie zawsze tu lądują.",
    kind: "text",
  },
  "week.mix.bofu": {
    key: "week.mix.bofu",
    label: "Cel BOFU %",
    hint: "Udział dołu lejka.",
    tip: "Głównie relacje Kasi, nie feed.",
    kind: "text",
  },
  "week.mix.tolerance": {
    key: "week.mix.tolerance",
    label: "Tolerancja mixu (pp)",
    hint: "Odchylenie w punktach procentowych zanim pojawi się ostrzeżenie.",
    tip: "10 = plus minus 10 punktów. Zatwierdzenie tygodnia i tak przechodzi.",
    kind: "text",
  },
  discussion_rounds: {
    key: "discussion_rounds",
    label: "Rundy panelu",
    hint: "Ile razy role gadają zanim powstanie copy.",
    tip: "1 = jeden JSON. Więcej rund to koszt Pro bez gwarancji lepszych slajdów.",
    kind: "text",
  },
  "memory.enabled": {
    key: "memory.enabled",
    label: "Pamięć wniosków",
    hint: "Czy retro w ogóle wraca do kolejnych karuzel.",
    tip: "Wyłączone: panel nie dostaje lekcji. Włączone: tylko to, co przepuszczą filtry poniżej.",
    kind: "select",
    options: BOOL_OPTS,
  },
  "memory.weight": {
    key: "memory.weight",
    label: "Siła pamięci",
    hint: "Jak mocno wnioski pchają kolejny brief.",
    tip: "low / medium / high. High łatwo zamyka kąty w jedną nutę.",
    kind: "select",
    options: [
      { value: "low", label: "niska" },
      { value: "medium", label: "średnia" },
      { value: "high", label: "wysoka" },
    ],
  },
  "memory.lookback_n": {
    key: "memory.lookback_n",
    label: "Ile ostatnich karuzel",
    hint: "Okno wstecz przy składaniu paczki pamięci.",
    tip: "Liczba, nie dni. 8 = osiem ostatnich z wnioskiem.",
    kind: "text",
  },
  "memory.min_rating": {
    key: "memory.min_rating",
    label: "Minimalna ocena",
    hint: "Od jakiej gwiazdki wniosek w ogóle wchodzi.",
    tip: "none = bez filtra oceny. hero = tylko to, co oznaczyłeś jako hit.",
    kind: "select",
    options: [
      { value: "none", label: "bez progu" },
      { value: "negative", label: "od negatywnej" },
      { value: "neutral", label: "od neutralnej" },
      { value: "positive", label: "od pozytywnej" },
      { value: "hero", label: "tylko hero" },
    ],
  },
  "memory.include_stats": {
    key: "memory.include_stats",
    label: "Dołącz statystyki",
    hint: "Zasięg, zapisy, score przy wniosku.",
    tip: "Panel widzi liczby z Graph, nie tylko tekst lekcji.",
    kind: "select",
    options: BOOL_OPTS,
  },
  "memory.include_negative_as_avoid": {
    key: "memory.include_negative_as_avoid",
    label: "Negatywne jako unikaj",
    hint: "Słaba ocena idzie do listy avoid, nie repeat.",
    tip: "Bez tego negatyw ginie albo uczy w złą stronę.",
    kind: "select",
    options: BOOL_OPTS,
  },
  "memory.only_approved_lessons": {
    key: "memory.only_approved_lessons",
    label: "Tylko zatwierdzone wnioski",
    hint: "Do panelu ról idą wyłącznie approved. Pending nie uczą modelu.",
    tip: "Retro zawsze tworzy pending. Ty zatwierdzasz na tej zakładce. To jest bezpiecznik, nie ozdoba.",
    kind: "select",
    options: BOOL_OPTS,
  },
  "prompt.concepts": {
    key: "prompt.concepts",
    label: "Prompt koncepcji",
    hint: "Instrukcja na 4 karty kątów.",
    tip: "Krok 1. Placeholdery briefu i źródeł. Nie pisze jeszcze slajdów.",
    kind: "prompt",
  },
  "prompt.discussion_panel": {
    key: "prompt.discussion_panel",
    label: "Prompt panelu ról",
    hint: "Jedna runda: córka, głos, architekt, narzędziarka, redaktor.",
    tip: "Wchodzi po wyborze karty. Dokleja pamięć, jeśli włączona.",
    kind: "prompt",
  },
  "prompt.copy_synth": {
    key: "prompt.copy_synth",
    label: "Prompt syntezy slajdów",
    hint: "Składa copy z panelu i promptu typu.",
    tip: "Ostatni slajd bierze z typu (reveal albo narzędzie), nie z tego pliku. W text/sub wolno - lista i **fraza** (marker), nie w caption.",
    kind: "prompt",
  },
  "prompt.rules_copy": {
    key: "prompt.rules_copy",
    label: "Reguły głosu slajdów",
    hint: "Koleżanka z sąsiedztwa. Czarna lista żargonu. 4-10 ścian.",
    tip: "Tylko panel, copy i recenzja. Nie wchodzi do 4 koncepcji.",
    kind: "prompt",
  },
  "prompt.copy_review": {
    key: "prompt.copy_review",
    label: "Prompt recenzji slajdów",
    hint: "Role oceniają gotowy tekst, nie koncepcję.",
    tip: "Po pierwszej generacji. Werdykt ok albo rewrite. Poprawka tylko na klik.",
    kind: "prompt",
  },
  "prompt.copy_revise": {
    key: "prompt.copy_revise",
    label: "Prompt poprawki po recenzji",
    hint: "Redaktor przepisuje slajdy według głosów ról.",
    tip: "Odpala się przyciskiem Popraw po recenzji. Potem znowu recenzja.",
    kind: "prompt",
  },
  "prompt.rules_shared": {
    key: "prompt.rules_shared",
    label: "Reguły wspólne",
    hint: "Doklejane do koncepcji, panelu, copy i retro.",
    tip: "Tu jest zakaz zmyślonych procentów i em dash. Zmiana tu idzie w cztery kroki naraz.",
    kind: "prompt",
  },
  "prompt.retro": {
    key: "prompt.retro",
    label: "Prompt retro",
    hint: "Jak model ma ocenić opublikowaną karuzelę.",
    tip: "Na żądanie. Wynik i tak ląduje jako pending. Dostaje snapshot, nie tylko copy.",
    kind: "prompt",
  },
  "prompt.layout_plan": {
    key: "prompt.layout_plan",
    label: "Prompt planu układu",
    hint: "Dobiera rytm i przepis na każdą ścianę ze szkicu.",
    tip: "Tor AI: Analizuj układ. Tor własny: pierwszy krok Zredaguj slajdy.",
    kind: "prompt",
  },
  "prompt.draft_place": {
    key: "prompt.draft_place",
    label: "Prompt wstawiania szkicu",
    hint: "Tnie szkic na ściany 1:1, bez nowego haka.",
    tip: "Drugi krok Wstaw: kładzie szkic na plan z analizy. Nie woła 4 koncepcji.",
    kind: "prompt",
  },
  "prompt.draft_caption": {
    key: "prompt.draft_caption",
    label: "Prompt opisu pod postem",
    hint: "Po wstawieniu ścian pisze caption i pytanie do komentarza.",
    tip: "Nie rusza zdań na slajdach. Link /k/… musi być w opisie.",
    kind: "prompt",
  },
  "prompt.draft_review": {
    key: "prompt.draft_review",
    label: "Prompt recenzji szkicu",
    hint: "Role ruszają zdanie tylko przy błędzie rozumowania.",
    tip: "Werdykt ok albo fix. Bez copy_revise.",
    kind: "prompt",
  },
  "prompt.draft_critique": {
    key: "prompt.draft_critique",
    label: "Prompt krytyki pomysłu",
    hint: "Scorecard pod zasięg: hak, emocja, swipe, CTA. Werdykt ship/fix/kill.",
    tip: "Przed place przy Zredaguj. Bez „fajnie napisane”.",
    kind: "prompt",
  },
  "prompt.role.bart": {
    key: "prompt.role.bart",
    label: "Rola: Bart (retro)",
    hint: "Czwarty głos na retro. Nic nie zapisuje sam.",
    tip: "Tylko uzasadnienie zmian. Arek zatwierdza overlay.",
    kind: "prompt",
  },
  "model.layout_plan": {
    key: "model.layout_plan",
    label: "Model planu układu",
    hint: "Flash do analizy szkicu i PNG.",
    tip: "Gdy padnie, studio i tak ma heurystykę z rytmu tekstu.",
    kind: "select",
    options: TEXT_MODEL_OPTIONS,
  },
  "prompt.role.corka": {
    key: "prompt.role.corka",
    label: "Rola: córka",
    hint: "Ciało i zdanie z domu, nie wykład.",
    tip: "Głos w panelu. Nie pisze slajdów sama.",
    kind: "prompt",
  },
  "prompt.role.glos": {
    key: "prompt.role.glos",
    label: "Rola: głos Kasi",
    hint: "Ton marki, konkret, zero coachingu.",
    tip: "Panel. Pilnuje, żeby nie zabrzmiało jak Instagram psychologii.",
    kind: "prompt",
  },
  "prompt.role.architekt": {
    key: "prompt.role.architekt",
    label: "Rola: architekt",
    hint: "Liczba slajdów i rodzaj close z typu.",
    tip: "Panel. Ma czytać prompt typu, nie zgadywać STOP vs odpowiedź.",
    kind: "prompt",
  },
  "prompt.role.narzedziarka": {
    key: "prompt.role.narzedziarka",
    label: "Rola: narzędziarka",
    hint: "Kiedy narzędzie z książki, a kiedy nie.",
    tip: "W story nie zjada reveal. W vs-echo pilnuje karty STOP.",
    kind: "prompt",
  },
  "prompt.role.redaktor": {
    key: "prompt.role.redaktor",
    label: "Rola: redaktor",
    hint: "Skrót, jeden hak, polski bez em dash.",
    tip: "Panel i synteza copy. Ostatnie cięcie przed slajdami.",
    kind: "prompt",
  },
  "prompt.role.analityczka": {
    key: "prompt.role.analityczka",
    label: "Rola: analityczka",
    hint: "Tylko retro. Liczby i werdykt, nie nowy hak.",
    tip: "Nie wchodzi w koncepcje ani copy.",
    kind: "prompt",
  },
  "prompt.role.dyrektorka": {
    key: "prompt.role.dyrektorka",
    label: "Rola: dyrektorka artystyczna",
    hint: "Po renderze ocenia serię: hierarchia, rytm, kanon papieru.",
    tip: "Nie rusza słów. Tylko skład i tło.",
    kind: "prompt",
  },
  "prompt.role.reklamowa": {
    key: "prompt.role.reklamowa",
    label: "Rola: reklamowa",
    hint: "Test 3 sekund, swipe, jeden gest dalej.",
    tip: "Nie pisze copy. Mówi, czy zestaw zatrzymuje kciuk.",
    kind: "prompt",
  },
  "prompt.design_review": {
    key: "prompt.design_review",
    label: "Prompt recenzji designu",
    hint: "Dwie role + akcje. Najpierw recipe / series_align, nie marker.",
    tip: "Idzie po PNG. Ksero trzech contentów to fix. Feedback operatora i pamięć designu wchodzą tu.",
    kind: "prompt",
  },
  "prompt.design_verify": {
    key: "prompt.design_verify",
    label: "Prompt weryfikacji designu",
    hint: "Druga tura vision po poprawkach.",
    tip: "Może dać max 4 kolejne akcje, potem stop.",
    kind: "prompt",
  },
};

export const CONCEPT_KEYS = ["model.concepts", "model.concepts.fallback", "model.layout_plan"] as const;
export const CONCEPT_PROMPT_KEYS = [
  "prompt.concepts",
  "prompt.layout_plan",
  "prompt.draft_place",
  "prompt.draft_caption",
  "prompt.draft_critique",
  "prompt.draft_review",
] as const;
export const ROLE_MODEL_KEYS = ["model.discussion", "model.heavy.fallback", "discussion_rounds"] as const;
export const MEMORY_KEYS = [
  "memory.enabled",
  "memory.weight",
  "memory.lookback_n",
  "memory.min_rating",
  "memory.include_stats",
  "memory.include_negative_as_avoid",
  "memory.only_approved_lessons",
] as const;
export const ROLE_PROMPT_KEYS = [
  "prompt.discussion_panel",
  "prompt.role.corka",
  "prompt.role.glos",
  "prompt.role.architekt",
  "prompt.role.narzedziarka",
  "prompt.role.redaktor",
] as const;
export const COPY_MODEL_KEYS = ["model.copy"] as const;
export const COPY_PROMPT_KEYS = [
  "prompt.copy_synth",
  "prompt.rules_copy",
  "prompt.copy_review",
  "prompt.copy_revise",
] as const;
export const IMAGE_KEYS = ["model.image", "model.image.fallback"] as const;
export const DESIGN_PROMPT_KEYS = [
  "prompt.design_review",
  "prompt.design_verify",
  "prompt.role.dyrektorka",
  "prompt.role.reklamowa",
] as const;
export const PHOTO_KEYS = ["model.vision"] as const;
export const WEEK_KEYS = ["week.mix.tofu", "week.mix.mofu", "week.mix.bofu", "week.mix.tolerance"] as const;
export const RETRO_MODEL_KEYS = ["model.retro"] as const;
export const RETRO_PROMPT_KEYS = ["prompt.retro", "prompt.role.analityczka", "prompt.role.bart"] as const;

export const LESSON_KIND_LABEL: Record<string, string> = {
  repeat: "powtarzaj",
  avoid: "unikaj",
  watch: "obserwuj",
  "design-repeat": "design: powtarzaj",
  "design-avoid": "design: unikaj",
  "design-watch": "design: obserwuj",
};

export const LESSON_STATUS_LABEL: Record<string, string> = {
  pending: "oczekuje",
  approved: "zatwierdzony",
  rejected: "odrzucony",
};

export const SOURCE_LABEL: Record<string, string> = {
  file: "plik",
  db: "baza",
};

export const PRICE_KIND_LABEL: Record<string, string> = {
  in: "wejście (1M tokenów)",
  out: "wyjście (1M tokenów)",
  image: "jedno tło",
};

export function fieldMeta(key: string): FieldMeta {
  return (
    FIELDS[key] || {
      key,
      label: key,
      hint: "",
      tip: "Klucz bez opisu. Wartość i tak idzie do carousel_settings.",
      kind: key.startsWith("prompt.") ? "prompt" : "text",
    }
  );
}

export function sourceLabel(source: string): string {
  return SOURCE_LABEL[source] || source;
}

export function lessonKindLabel(kind: string): string {
  return LESSON_KIND_LABEL[kind] || kind;
}

export function lessonStatusLabel(status: string): string {
  return LESSON_STATUS_LABEL[status] || status;
}

export function parsePricingKey(key: string): { model: string; kind: string } | null {
  if (!key.startsWith("pricing.")) return null;
  const rest = key.slice("pricing.".length);
  const split = rest.lastIndexOf(".");
  if (split <= 0) return null;
  return { model: rest.slice(0, split), kind: rest.slice(split + 1) };
}

export function groupPricing(keys: string[]): { model: string; items: { key: string; kind: string }[] }[] {
  const map = new Map<string, { key: string; kind: string }[]>();
  for (const key of keys) {
    const parsed = parsePricingKey(key);
    if (!parsed) continue;
    const list = map.get(parsed.model) || [];
    list.push({ key, kind: parsed.kind });
    map.set(parsed.model, list);
  }
  return [...map.entries()].map(([model, items]) => ({ model, items }));
}

export function tabById(id: SettingsTab): TabMeta {
  return TABS.find((tab) => tab.id === id) || TABS[0];
}

export const FALLBACK_TYPES: { id: string; label: string; close: string; prompt_key: string }[] = [
  { id: "vs-echo", label: "Kontrast / echo", close: "tool", prompt_key: "prompt.type.vs-echo" },
  { id: "signals", label: "3 sygnały", close: "tool", prompt_key: "prompt.type.signals" },
  { id: "steps-save", label: "Kroki / ściągawka", close: "action", prompt_key: "prompt.type.steps-save" },
  { id: "swap", label: "Nie rób / zrób", close: "sentence", prompt_key: "prompt.type.swap" },
  { id: "tool", label: "Narzędzie z książki", close: "tool", prompt_key: "prompt.type.tool" },
  { id: "story", label: "Historyjka", close: "reveal", prompt_key: "prompt.type.story" },
  { id: "question-list", label: "Pytanie-lista", close: "sentence", prompt_key: "prompt.type.question-list" },
];
